import { type ReactNode, type FC, memo } from 'react';
import { twMerge } from 'tailwind-merge';
import { Text } from 'Text';

type GapYOption = '1.5' | '2.5' | '4';

type Props = {
    header?: string | null;
    children: ReactNode;
    className?: string;
    fullWidth?: boolean;
    gapY?: GapYOption;
    tabletGapY?: GapYOption;
    desktopGapY?: GapYOption;
};

function getGapYClass({
    gapY,
    tabletGapY,
    desktopGapY,
}: {
    gapY: GapYOption;
    tabletGapY?: GapYOption;
    desktopGapY?: GapYOption;
}) {
    const gapYMap = {
        default: {
            1.5: 'gap-y-1.5',
            2.5: 'gap-y-2.5',
            4: 'gap-y-4',
        },
        tablet: {
            1.5: 'sm:gap-y-1.5',
            2.5: 'sm:gap-y-2.5',
            4: 'sm:gap-y-4',
        },
        desktop: {
            1.5: 'md:gap-y-1.5',
            2.5: 'md:gap-y-2.5',
            4: 'md:gap-y-4',
        },
    };

    const gapYClass = gapYMap.default[gapY];

    const tabletGapYClass = tabletGapY ? gapYMap.tablet[tabletGapY] : '';
    const desktopGapYClass = desktopGapY ? gapYMap.desktop[desktopGapY] : '';

    return `${gapYClass} ${tabletGapYClass} ${desktopGapYClass}`.trim();
}

export const ContentItemBoxListContainer: FC<Props> = memo(
    ({ header, children, className, fullWidth, gapY = '4', tabletGapY, desktopGapY }) => (
        <div>
            {header && (
                <Text as="h2" weight="semibold" color="black" uppercase className="px-3 pb-5">
                    {header}
                </Text>
            )}

            <div
                className={twMerge(
                    'flex flex-col',
                    getGapYClass({ gapY, tabletGapY, desktopGapY }),
                    'md:grid md:gap-x-5',
                    fullWidth ? 'md:grid-cols-1' : 'md:grid-cols-2',
                    className,
                )}
            >
                {children}
            </div>
        </div>
    ),
);
